// The Your Agents card pile — which of the team's cards are in the stack, and in
// which order.
//
// The pile is drawn back-to-front: every card overlaps the one before it and only
// the LAST one is fully open, so the focused card is the last element of `visible`.
// The Maestro is the team lead and the pile's resting front card, mirroring native,
// where it is always carousel card 0 (`AgentsScreen.swift` `cards = [.maestro] + members`).
//
// The whole team is shown — the pile absorbs a bigger roster by tightening the overlap
// (the `--pile-behind` count drives the CSS strip height in `AgentPile.module.css`) down
// to a dense 32px floor, and past that the column simply grows off the row. There is no cutoff
// and no "{n} more" line: every agent gets a card, matching native's carousel
// (`AgentsScreen.swift`, which caps nothing) and the Figma (13578:234464 piles the whole
// team). Selecting an agent on the genome focuses its card and slides it to the front.

/** The reference depth: at or below this the strips draw at their full height; past
 *  it the CSS compresses them into the same footprint. Kept for the geometry docs. */
export const PILE_SIZE = 5;

/** The pile's stable id for the Maestro, which is not one of the BFF's sub-agents. */
export const MAESTRO_PILE_ID = '__maestro__';

export interface PileLayout {
  /** Back-to-front: the focused card is last, and the only one fully open. */
  visible: string[];
}

/**
 * Lay out the pile for a team and a focus.
 *
 * The focused card always ends up at the front, and the cards behind it keep the
 * roster's own order (Maestro first). An unknown or missing focus falls back to
 * the Maestro, so a stale selection can never empty the stack — and an agent past
 * the shown range counts as unknown, because a focus with no card in the pile would
 * leave the stack resting on nothing.
 *
 * `limit` caps how many team agents get a card; leaving it undefined shows the WHOLE
 * team. The collapsed pile passes a smaller limit so the stack shows a readable few and
 * offers a "View all" toggle that drops the limit and reveals the rest.
 */
export function pileLayout(agentIds: string[], focusedId?: string | null, limit?: number): PileLayout {
  const cap = limit === undefined ? agentIds.length : Math.max(limit, 1);
  const shown = agentIds.slice(0, cap);
  const all = [MAESTRO_PILE_ID, ...shown];
  const focused = focusedId && all.includes(focusedId) ? focusedId : MAESTRO_PILE_ID;
  const behind = all.filter((id) => id !== focused);
  return { visible: [...behind, focused] };
}
